/**
 * Triage page — the vendor-side CRA workflow.
 *
 * A VulnerabilityEvent is created by the KEV scan whenever an actively exploited
 * CVE appears in a KomponenteVersion that ships inside one of our products. The
 * human step this page exists for is the applicability verdict: BETROFFEN or
 * NICHT_BETROFFEN. Only a BETROFFEN verdict fans out Benachrichtigung cards for
 * the affected customers, and only those cards can be stamped as sent — that
 * chain is the evidence trail for the market surveillance authority.
 *
 * Contract details that shape the code below:
 *   - Every timestamp in this payload is a RAW `CmdbDateTime` (ISO,
 *     'dd/MM/yyyy HH:mm:ss' or epoch millis). All of them go through
 *     `fmtDate`/`fmtDay`; `new Date(x)` alone would render 'Invalid Date'.
 *   - `cve.base_score` may be a string — compared only via `parseScore`.
 *   - The stored verdict has four values, the submittable one has two. The
 *     dialog offers exactly the two.
 *   - After a verdict or notification mutation the affected row is REFETCHED
 *     from the queue and patched into state. Advisory drafts and evidence use
 *     separate per-event endpoints in the dialog.
 *   - Mutating controls are gated on `triage.write` / `notification.send`. An
 *     auditor sees every row, the full audit trail and every draft, and no
 *     enabled action.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import CveLink from '../components/CveLink';
import Banner from '../components/Banner';
import Chip, { ChipList } from '../components/Chip';
import CvssScore from '../components/CvssScore';
import DataTable, { type DataTableColumn } from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import Icon from '../components/Icon';
import { KpiRow, type KpiItem } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import { PageSpinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import { errorMessage, fetchVulnerabilityEvents } from '../lib/api';
import { useAuth } from '../lib/auth';
import { DASH, countLabel, fmtDate, fmtDay, fmtValue, truncate } from '../lib/format';
import { missingPermissionTitle, readOnlyNotice } from '../lib/permissions';
import type {
  Bewertung,
  BewertungResult,
  KpiCounter,
  TriageDocumentSummary,
  VulnerabilityEventRow,
  VulnerabilityEventsResponse,
} from '../lib/types';

import BewertungDialog, { type AssessmentTab } from './triage/BewertungDialog';
import NotificationDialog from './triage/NotificationDialog';
import { DocumentIcon } from './triage/TriageDocumentPanels';
import {
  EventAuditTrail,
  EventCveFacts,
  EventInstanzen,
  EventScopeFacts,
} from './triage/EventFacts';
import {
  BEWERTUNG_LABELS,
  BEWERTUNG_TONE,
  kundenOf,
  matchesSearch,
  parseScore,
  produktVersionLabel,
} from './triage/eventHelpers';

import './triage/triage.css';

/** How many customer names fit into a table cell before the '+N' overflow. */
const KUNDEN_VISIBLE = 3;

const SCORE_OPTIONS = [
  { value: '', label: 'Min. CVSS: alle' },
  { value: '4', label: '≥ 4,0 (mittel)' },
  { value: '7', label: '≥ 7,0 (hoch)' },
  { value: '9', label: '≥ 9,0 (kritisch)' },
] as const;

/** Which assessments the queue shows; MAIL = notification still to send. */
type BewertungFilter = Bewertung | 'MAIL' | 'ALLE';

interface Filters {
  bewertung: BewertungFilter;
  search: string;
  /** Empty string = no CVSS floor. Kept as a string because it comes from a <select>. */
  minScore: string;
  /** 'YYYY-MM-DD' — events whose KEV date is older are hidden. */
  kevFrom: string;
  onlyKevListed: boolean;
  /** Minimum number of distinct affected customers. */
  minKunden: string;
}

const INITIAL_FILTERS: Filters = {
  bewertung: 'OFFEN',
  search: '',
  minScore: '',
  kevFrom: '',
  onlyKevListed: false,
  minKunden: '',
};

/**
 * One select for assessment and the KEV-list flag. Every assessment is a
 * choice of its own; „KEV-gelistet“ combines with „Offen“ and „Alle“ as before.
 */
type StatusFilter = 'open' | 'open-kev' | 'betroffen' | 'mail' | 'nicht' | 'behoben' | 'all' | 'kev';

const STATUS_FILTERS: Readonly<Record<StatusFilter, { label: string; flags: Pick<Filters, 'bewertung' | 'onlyKevListed'> }>> = {
  open: { label: 'Nur offene', flags: { bewertung: 'OFFEN', onlyKevListed: false } },
  'open-kev': { label: 'Offene, KEV-gelistet', flags: { bewertung: 'OFFEN', onlyKevListed: true } },
  betroffen: { label: 'Betroffen', flags: { bewertung: 'BETROFFEN', onlyKevListed: false } },
  mail: { label: 'Benachrichtigung offen', flags: { bewertung: 'MAIL', onlyKevListed: false } },
  nicht: { label: 'Nicht betroffen', flags: { bewertung: 'NICHT_BETROFFEN', onlyKevListed: false } },
  behoben: { label: 'Behoben', flags: { bewertung: 'BEHOBEN', onlyKevListed: false } },
  all: { label: 'Alle Bewertungen', flags: { bewertung: 'ALLE', onlyKevListed: false } },
  kev: { label: 'Alle, KEV-gelistet', flags: { bewertung: 'ALLE', onlyKevListed: true } },
};

function sameFilters(a: Filters, b: Filters): boolean {
  return (Object.keys(a) as (keyof Filters)[]).every((key) => a[key] === b[key]);
}

function statusFilterValue(filters: Filters): StatusFilter {
  const keys = Object.keys(STATUS_FILTERS) as StatusFilter[];
  return (
    keys.find((key) => {
      const flags = STATUS_FILTERS[key].flags;
      return flags.bewertung === filters.bewertung && flags.onlyKevListed === filters.onlyKevListed;
    }) ??
    keys.find((key) => STATUS_FILTERS[key].flags.bewertung === filters.bewertung) ??
    'all'
  );
}

function matchesBewertung(event: VulnerabilityEventRow, bewertung: BewertungFilter): boolean {
  if (bewertung === 'ALLE') return true;
  if (bewertung === 'MAIL') return event.benachrichtigungen_offen > 0;
  return event.bewertung === bewertung;
}

function sumBy(
  events: readonly VulnerabilityEventRow[],
  pick: (event: VulnerabilityEventRow) => number,
): number {
  return events.reduce((total, event) => total + pick(event), 0);
}

/** Parses a filter input that may be empty or junk into a numeric floor. */
function numericFloor(raw: string): number | null {
  if (raw.trim() === '') return null;
  const parsed = Number.parseFloat(raw.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

/** Current native KEV state; unlike the CVE card flag this is not persisted. */
function hasNativeKevAssertion(event: VulnerabilityEventRow): boolean {
  return event.kev.sources.some((source) => Boolean(source?.trim()));
}

export function TriagePage(): ReactNode {
  const { user, has } = useAuth();
  const toast = useToast();

  const [data, setData] = useState<VulnerabilityEventsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [searchParams] = useSearchParams();
  // `?q=` deep links (from the Audit-Log) open the queue on one case, decided
  // or not — so the 'Nur offene' default must not hide it.
  const [filters, setFilters] = useState<Filters>(() => {
    const q = searchParams.get('q')?.trim() ?? '';
    return q === '' ? INITIAL_FILTERS : { ...INITIAL_FILTERS, bewertung: 'ALLE', search: q };
  });
  const [bewertungEventId, setBewertungEventId] = useState<number | null>(null);
  const [assessmentTab, setAssessmentTab] = useState<AssessmentTab>('assessment');
  const [mailEventId, setMailEventId] = useState<number | null>(null);

  // Guards a late response from overwriting a newer one.
  const requestSeq = useRef(0);

  const canTriage = has('triage.write');
  const canSend = has('notification.send');
  const readOnly = !canTriage && !canSend;
  const bewerter = user?.name?.trim() || user?.email || 'unbekannt';

  const load = useCallback(async (): Promise<void> => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const response = await fetchVulnerabilityEvents();
      if (seq !== requestSeq.current) return;
      setData(response);
      setLoadError(null);
      setLoadedAt(new Date());
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Refetch after a mutation and patch exactly the affected row back into state.
   * There is no per-event GET, so the list is the source; patching one row keeps
   * the other rows, the sort order and the expanded details stable while still
   * showing only server-confirmed values.
   */
  const refreshEvent = useCallback(async (eventId: number): Promise<void> => {
    const seq = ++requestSeq.current;
    const fresh = await fetchVulnerabilityEvents();
    // A full reload started meanwhile owns the state — drop this result.
    if (seq !== requestSeq.current) return;
    setData((current) => {
      if (current === null) return fresh;
      const updated = fresh.events.find((event) => event.id === eventId);
      const events =
        updated === undefined
          ? current.events.filter((event) => event.id !== eventId)
          : current.events.map((event) => (event.id === eventId ? updated : event));
      return { count: fresh.count, offen: fresh.offen, events };
    });
    setLoadedAt(new Date());
  }, []);

  // The dialog reports confirmed saves and loads so closing it leaves the
  // queue count current without a second request or a verdict submission.
  const handleDocumentsChanged = useCallback(
    (eventId: number, summary: TriageDocumentSummary): void => {
      setData((current) => {
        if (current === null) return current;
        const existing = current.events.find((event) => event.id === eventId);
        if (existing === undefined || (
          existing.dokumente?.advisory === summary.advisory &&
          existing.dokumente?.attachments === summary.attachments
        )) return current;
        return {
          ...current,
          events: current.events.map((event) => event.id === eventId
            ? { ...event, dokumente: summary }
            : event),
        };
      });
    },
    [],
  );

  // Memoised so the derived lists below only recompute when the payload changes.
  const events = useMemo<readonly VulnerabilityEventRow[]>(() => data?.events ?? [], [data]);

  const bewertungEvent = useMemo(
    () => events.find((event) => event.id === bewertungEventId) ?? null,
    [events, bewertungEventId],
  );
  const mailEvent = useMemo(
    () => events.find((event) => event.id === mailEventId) ?? null,
    [events, mailEventId],
  );

  const handleSaved = useCallback(
    (result: BewertungResult): void => {
      setBewertungEventId(null);
      const fanOut = result.benachrichtigungen;
      const verdict = BEWERTUNG_LABELS[result.bewertung];
      const parts = [`Bewertung gespeichert: ${verdict}.`];
      if (fanOut !== undefined) {
        parts.push(
          `${fanOut.created} Benachrichtigung(en) neu angelegt, ${fanOut.existing} bereits vorhanden.`,
        );
      }
      // With the default 'Nur offene' filter the row leaves the view the moment
      // it is assessed — say so, because the notification step is still pending.
      if (result.bewertung === 'BETROFFEN' && canSend && filters.bewertung === 'OFFEN') {
        parts.push('Zum Benachrichtigen im Statusfilter „Betroffen“ oder „Benachrichtigung offen“ wählen.');
      }
      toast.success(parts.join(' '));
      if (fanOut !== undefined && fanOut.errors.length > 0) {
        toast.error(`Einige Benachrichtigungen konnten nicht angelegt werden: ${fanOut.errors.join(' · ')}`);
      }
      void refreshEvent(result.id).catch((err: unknown) => {
        toast.error(`Zeile konnte nicht neu geladen werden: ${errorMessage(err)}`);
      });
    },
    [canSend, filters.bewertung, refreshEvent, toast],
  );

  const handleStamped = useCallback((): void => {
    if (mailEventId === null) return;
    toast.success('Benachrichtigung als versendet markiert.');
    void refreshEvent(mailEventId).catch((err: unknown) => {
      toast.error(`Zeile konnte nicht neu geladen werden: ${errorMessage(err)}`);
    });
  }, [mailEventId, refreshEvent, toast]);

  const counters = useMemo<KpiCounter[]>(() => {
    const betroffen = events.filter((event) => event.bewertung === 'BETROFFEN').length;
    const nichtBetroffen = events.filter((event) => event.bewertung === 'NICHT_BETROFFEN').length;
    const behoben = events.filter((event) => event.bewertung === 'BEHOBEN').length;
    const offeneMails = sumBy(events, (event) => event.benachrichtigungen_offen);
    const mitOffenerMail = events.filter((event) => event.benachrichtigungen_offen > 0).length;
    const versendeteMails = sumBy(events, (event) => event.benachrichtigungen_versendet);
    return [
      {
        label: 'Events',
        value: data?.count ?? events.length,
        tone: 'neutral',
        title: 'KEV-Treffer in Komponentenversionen insgesamt',
      },
      {
        label: 'Offene Bewertungen',
        value: data?.offen ?? 0,
        tone: (data?.offen ?? 0) > 0 ? 'warn' : 'good',
        title: 'Events, deren Bewertung noch offen ist',
      },
      {
        label: 'Betroffen',
        value: betroffen,
        tone: betroffen > 0 ? 'alert' : 'good',
        title: 'CVE als anwendbar bewertet, Kunden müssen benachrichtigt werden',
      },
      {
        label: 'Nicht betroffen',
        value: nichtBetroffen,
        tone: 'neutral',
        title: 'CVE als nicht anwendbar bewertet (mit Begründung im Audit-Trail)',
      },
      {
        label: 'Behoben',
        value: behoben,
        tone: 'good',
        title: 'Vom KEV-Scan automatisch geschlossen (Komponente nicht mehr betroffen)',
      },
      {
        // Counted per event, like the other tiles, so its filter shows exactly
        // this many rows; the notification totals go in the tooltip.
        label: 'Benachrichtigung offen',
        value: mitOffenerMail,
        tone: mitOffenerMail > 0 ? 'warn' : 'good',
        title: `${offeneMails} Benachrichtigung(en) offen, ${versendeteMails} bereits als versendet gestempelt`,
      },
    ];
  }, [data, events]);

  // Every tile maps onto the assessment filter: each opens its whole category
  // (other filters cleared) and stays marked while exactly it is active. A
  // second click goes back to the default (open ones); on „Offene“, which is
  // the default, it shows all.
  const kpiTiles = useMemo((): KpiItem[] => {
    const only = (bewertung: BewertungFilter): Filters => ({ ...INITIAL_FILTERS, bewertung });
    const targets: Readonly<Record<string, Filters>> = {
      Events: only('ALLE'),
      'Offene Bewertungen': INITIAL_FILTERS,
      Betroffen: only('BETROFFEN'),
      'Nicht betroffen': only('NICHT_BETROFFEN'),
      Behoben: only('BEHOBEN'),
      'Benachrichtigung offen': only('MAIL'),
    };
    return counters.map((counter) => {
      const target = targets[counter.label];
      if (!target) return counter;
      const active = sameFilters(filters, target);
      const cleared = target === INITIAL_FILTERS ? only('ALLE') : INITIAL_FILTERS;
      return {
        ...counter,
        ariaLabel: `${counter.label}: ${counter.value} anzeigen`,
        selected: active,
        controls: 'triage-results',
        onClick: () => setFilters(active ? cleared : target),
      };
    });
  }, [counters, filters]);

  const visibleRows = useMemo<VulnerabilityEventRow[]>(() => {
    const scoreFloor = numericFloor(filters.minScore);
    const kundenFloor = numericFloor(filters.minKunden);
    return events.filter((event) => {
      if (!matchesBewertung(event, filters.bewertung)) return false;
      if (!matchesSearch(event, filters.search)) return false;
      if (scoreFloor !== null) {
        const score = parseScore(event.cve.base_score);
        // Events without a score are hidden as soon as a floor is demanded —
        // they cannot be proven to satisfy it.
        if (score === null || score < scoreFloor) return false;
      }
      if (filters.onlyKevListed && !hasNativeKevAssertion(event)) return false;
      if (filters.kevFrom !== '') {
        const added = event.kev.aufgenommen_am;
        // Both sides are 'YYYY-MM-DD', so a string compare is a date compare.
        if (added === null || added < filters.kevFrom) return false;
      }
      if (kundenFloor !== null && event.betroffene_kunden < kundenFloor) return false;
      return true;
    });
  }, [events, filters]);

  const columns = useMemo<DataTableColumn<VulnerabilityEventRow>[]>(() => {
    /** Document reads are available to everyone with access to this queue. */
    function renderActions(event: VulnerabilityEventRow): ReactNode {
      const isBetroffen = event.bewertung === 'BETROFFEN';
      const bewertenLabel = event.bewertung === 'OFFEN' ? 'Bewerten' : 'Neu bewerten';
      const documents = event.dokumente;
      const documentCount = documents == null ? null : Number(documents.advisory) + documents.attachments;
      const documentDescription = documents == null
        ? 'Status nicht verfügbar'
        : documentCount === 0
          ? 'Keine Dokumente'
          : [
            documents.advisory ? 'Advisory' : null,
            documents.attachments > 0
              ? `${documents.attachments} ${documents.attachments === 1 ? 'Anhang' : 'Anhänge'}`
              : null,
          ].filter(Boolean).join(' · ');
      return (
        <div className="triage-actions">
          {canTriage ? (
            <button
              type="button"
              className={event.bewertung === 'OFFEN' ? 'primary small' : 'ghost small'}
              onClick={(clickEvent) => {
                // The row itself toggles the detail area — do not fold it here.
                clickEvent.stopPropagation();
                setAssessmentTab('assessment');
                setBewertungEventId(event.id);
              }}
            >
              {bewertenLabel}
            </button>
          ) : (
            <button
              type="button"
              className="ghost small"
              disabled
              title={missingPermissionTitle('triage.write')}
            >
              {bewertenLabel}
            </button>
          )}
          <button
            type="button"
            className={`ghost small triage-documents-button${documentCount === null ? '' : documentCount > 0 ? ' has-documents' : ' no-documents'}`}
            aria-label={`Dokumente: ${documentDescription}`}
            onClick={(clickEvent) => {
              clickEvent.stopPropagation();
              setAssessmentTab(documents && !documents.advisory && documents.attachments > 0
                ? 'attachments' : 'assessment');
              setBewertungEventId(event.id);
            }}
          >
            <span className="triage-documents-title">
              <DocumentIcon /><span>Dokumente</span>
              {documentCount === null ? null : <Chip>{documentCount}</Chip>}
            </span>
            <span className="triage-documents-summary">{documentDescription}</span>
          </button>
          {isBetroffen ? (
            canSend ? (
              <button
                type="button"
                className="primary small"
                onClick={(clickEvent) => {
                  clickEvent.stopPropagation();
                  setMailEventId(event.id);
                }}
                title="Benachrichtigungen erzeugen und Entwürfe anzeigen"
              >
                <Icon name="mail" /> Kunden benachrichtigen
              </button>
            ) : (
              <button
                type="button"
                className="ghost small"
                disabled
                title={missingPermissionTitle('notification.send')}
              >
                <Icon name="mail" /> Kunden benachrichtigen
              </button>
            )
          ) : null}
        </div>
      );
    }

    return [
      {
        key: 'cve',
        header: 'CVE',
        width: '16%',
        sortValue: (event) => event.cve.id,
        render: (event) => (
          <div className="triage-cve">
            <ChipList>
              <CveLink cve={event.cve.id} />
              {event.cve.aktiv_ausgenutzt ? (
                <Chip tone="warn" title="Aktiv ausgenutzt">
                  aktiv
                </Chip>
              ) : null}
            </ChipList>
            {event.cve.beschreibung ? (
              <div className="prod" title={event.cve.beschreibung}>
                {truncate(event.cve.beschreibung, 70)}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: 'score',
        header: 'CVSS',
        align: 'right',
        width: '7%',
        headerTitle: 'CVSS Base Score',
        sortValue: (event) => parseScore(event.cve.base_score),
        render: (event) => <CvssScore score={parseScore(event.cve.base_score)} />,
      },
      {
        key: 'kev',
        header: 'KEV seit',
        width: '10%',
        headerTitle: 'Publikationsdatum der bevorzugten nativen KEV-Assertion',
        sortValue: (event) => event.kev.aufgenommen_am,
        render: (event) => {
          if (event.kev.aufgenommen_am === null) {
            return hasNativeKevAssertion(event)
              ? <Chip tone="alert" title="KEV-Assertion ohne Publikationsdatum">gelistet</Chip>
              : <span className="muted" title="Keine KEV-Assertion oder Abruf fehlgeschlagen">{DASH}</span>;
          }
          return (
            <div>
              {fmtDay(event.kev.aufgenommen_am)}
              {event.kev.frist ? <div className="prod">Frist {fmtDay(event.kev.frist)}</div> : null}
              {event.kev.ransomware === 'Known' ? (
                <div className="prod triage-ransom">Ransomware bekannt</div>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'komponente',
        header: 'Komponente / Produkte',
        sortValue: (event) => event.komponente.name,
        render: (event) => (
          <div>
            <div className="kunde-name">
              {fmtValue(event.komponente.name)}{' '}
              <span className="muted">{event.komponente.version ?? ''}</span>
            </div>
            <div className="prod">
              {event.komponente.hersteller ? `${event.komponente.hersteller} · ` : ''}
              {event.produktversionen.length === 0
                ? 'in keiner Produktversion'
                : `in ${event.produktversionen.map(produktVersionLabel).join(', ')}`}
            </div>
          </div>
        ),
      },
      {
        key: 'kunden',
        header: 'Betroffene Kunden',
        width: '14%',
        headerTitle: 'Anzahl unterschiedlicher Kunden mit betroffenen Produktinstanzen',
        sortValue: (event) => event.betroffene_kunden,
        render: (event) => {
          const kunden = kundenOf(event);
          if (kunden.length === 0) {
            return <span className="muted">keine</span>;
          }
          const shown = kunden.slice(0, KUNDEN_VISIBLE);
          const hidden = kunden.slice(KUNDEN_VISIBLE);
          const hiddenNames = hidden.map((kunde) => kunde.name ?? '?').join(', ');
          return (
            <div>
              <span className="kunde-name">
                {shown.map((kunde) => kunde.name ?? '?').join(', ')}
              </span>
              {hidden.length > 0 ? (
                <>
                  {' '}
                  <Chip title={`Weitere Kunden: ${hiddenNames}`}>+{hidden.length}</Chip>
                </>
              ) : null}
              <div className="prod">
                {event.betroffene.length}{' '}
                {event.betroffene.length === 1 ? 'Produktinstanz' : 'Produktinstanzen'}
              </div>
            </div>
          );
        },
      },
      {
        key: 'bewertung',
        header: 'Bewertung',
        width: '13%',
        sortValue: (event) => event.bewertung,
        render: (event) => (
          <div>
            <Chip tone={BEWERTUNG_TONE[event.bewertung]}>{BEWERTUNG_LABELS[event.bewertung]}</Chip>
            {event.bewertet_von || event.bewertet_am ? (
              <div className="prod">
                {fmtValue(event.bewertet_von)} · {fmtDate(event.bewertet_am)}
              </div>
            ) : null}
            {event.begruendung ? (
              <div className="prod triage-begruendung" title={event.begruendung}>
                „{truncate(event.begruendung, 60)}“
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: 'benachrichtigung',
        header: 'Benachrichtigung',
        width: '11%',
        sortValue: (event) => event.benachrichtigungen_offen,
        render: (event) => {
          const versendet = event.benachrichtigungen_versendet;
          const offen = event.benachrichtigungen_offen;
          if (versendet === 0 && offen === 0) {
            return event.bewertung === 'BETROFFEN' ? (
              <span className="muted">ausstehend</span>
            ) : (
              <span className="muted">{DASH}</span>
            );
          }
          return (
            <div>
              <Chip tone="good">{versendet} versendet</Chip>
              {offen > 0 ? <div className="prod">{offen} ausstehend</div> : null}
            </div>
          );
        },
      },
      {
        key: 'aktion',
        header: 'Aktion',
        width: '15%',
        render: renderActions,
      },
    ];
  }, [canSend, canTriage]);

  if (loading && data === null) {
    return <PageSpinner text="Bewertungs-Warteschlange wird geladen…" />;
  }

  return (
    <>
      <PageHeader
        title="KEV-Bewertungen"
        sub={
          loadedAt === null
            ? undefined
            : `Stand ${loadedAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
        }
      />

      {loadError === null ? null : (
        <Banner kind="error" sub="Prüfen Sie die Verbindung zu CMDBuild.">
          {loadError}
        </Banner>
      )}

      {readOnly ? (
        <Banner kind="info">{readOnlyNotice('triage.write', 'notification.send')}</Banner>
      ) : null}

      <KpiRow counters={kpiTiles} />

      <section id="triage-results" className="card">
        <div className="card-head table-head">
          <h2 title="Standardsortierung: offene Bewertungen zuerst, danach nach Event-ID absteigend.">
            Warteschlange
          </h2>
        </div>
        <div className="table-toolbar">
          <input
            type="search"
            className="triage-search"
            value={filters.search}
            onChange={(changeEvent) =>
              setFilters((current) => ({ ...current, search: changeEvent.target.value }))
            }
            placeholder="CVE, Komponente, Kunde suchen…"
            aria-label="Warteschlange filtern"
          />
          <select
            value={statusFilterValue(filters)}
            onChange={(changeEvent) =>
              setFilters((current) => ({
                ...current,
                ...STATUS_FILTERS[changeEvent.target.value as StatusFilter].flags,
              }))
            }
            aria-label="Nach Status filtern"
          >
            {(Object.keys(STATUS_FILTERS) as StatusFilter[]).map((key) => (
              <option key={key} value={key}>
                {STATUS_FILTERS[key].label}
              </option>
            ))}
          </select>
          <select
            value={filters.minScore}
            onChange={(changeEvent) =>
              setFilters((current) => ({ ...current, minScore: changeEvent.target.value }))
            }
            aria-label="Min. CVSS"
          >
            {SCORE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <label className="triage-inline-field">
            <span>KEV ab</span>
            <input
              type="date"
              value={filters.kevFrom}
              onChange={(changeEvent) =>
                setFilters((current) => ({ ...current, kevFrom: changeEvent.target.value }))
              }
              aria-label="KEV-Aufnahme ab"
            />
          </label>
          <input
            type="number"
            className="triage-min-kunden"
            min={0}
            step={1}
            value={filters.minKunden}
            onChange={(changeEvent) =>
              setFilters((current) => ({ ...current, minKunden: changeEvent.target.value }))
            }
            placeholder="Min. Kunden"
            aria-label="Min. Kunden"
            title="Mindestanzahl betroffener Kunden"
          />
          <div className="table-toolbar-options">
            {filters.search !== INITIAL_FILTERS.search ||
            filters.bewertung !== INITIAL_FILTERS.bewertung ||
            filters.onlyKevListed !== INITIAL_FILTERS.onlyKevListed ||
            filters.minScore !== INITIAL_FILTERS.minScore ||
            filters.kevFrom !== INITIAL_FILTERS.kevFrom ||
            filters.minKunden !== INITIAL_FILTERS.minKunden ? (
              <button type="button" className="link" onClick={() => setFilters(INITIAL_FILTERS)}>
                Filter zurücksetzen
              </button>
            ) : null}
            <span className="table-count" aria-live="polite">
              {visibleRows.length === events.length
                ? countLabel(events.length, 'Bewertung', 'Bewertungen')
                : `${visibleRows.length} von ${countLabel(events.length, 'Bewertung', 'Bewertungen')}`}
            </span>
          </div>
        </div>

        {events.length === 0 && loadError === null ? (
          <EmptyState title="Noch keine KEV-Bewertungen vorhanden">
            Bewertungen entstehen nach dem KEV-Abgleich für betroffene ausgelieferte Komponenten.
          </EmptyState>
        ) : (
          <DataTable
            rows={visibleRows}
            columns={columns}
            rowKey={(event) => event.id}
            mobileBreakpoint={960}
            pageSize={10}
            paginationResetKey={JSON.stringify(filters)}
            resetPageOnRowsChange={false}
            revealRowKey={bewertungEventId ?? mailEventId}
            caption="Bewertungs-Warteschlange der KEV-Treffer"
            emptyText="Keine Bewertungen für die aktuelle Auswahl. Filter anpassen oder im Statusfilter „Alle Bewertungen“ wählen."
            rowClassName={(event) => (event.bewertung === 'OFFEN' ? 'triage-row-open' : undefined)}
            renderDetail={(event) => (
              <div className="triage-detail">
                <EventCveFacts event={event} />
                <EventScopeFacts event={event} />
                <EventAuditTrail event={event} />
                <EventInstanzen event={event} />
              </div>
            )}
          />
        )}
      </section>

      {bewertungEvent === null ? null : (
        <BewertungDialog
          key={bewertungEvent.id}
          event={bewertungEvent}
          bewerter={bewerter}
          canWrite={canTriage}
          initialTab={assessmentTab}
          onClose={() => setBewertungEventId(null)}
          onSaved={handleSaved}
          onDocumentsChanged={handleDocumentsChanged}
        />
      )}

      {mailEvent === null ? null : (
        <NotificationDialog
          event={mailEvent}
          canSend={canSend}
          onClose={() => setMailEventId(null)}
          onStamped={handleStamped}
        />
      )}
    </>
  );
}

export default TriagePage;
